import { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useResourcePerformanceObserver } from '../../../hooks/usePerformanceObserver';
import { getUserData } from '../../../redux/selectors/userData';
import { datadogLogs } from '@datadog/browser-logs';

const VelocityWeatherLogger: FunctionComponent = () => {
  const userData = useSelector(getUserData);
  const isPublicUser = userData?.id === undefined ? true : false;
  if (!('PerformanceObserver' in window)) {
    datadogLogs.logger.info('velocity-weather-api-call-monitoring-not-supported', {
      userId: isPublicUser ? 'public' : userData?.id
    });
    return null;
  }

  if (
    datadogLogs.getInitConfiguration()?.clientToken === undefined ||
    datadogLogs.getInitConfiguration()?.site === undefined
  ) {
    console.error('Datadog logs not initialized');
    return null;
  }

  const resourcePerformanceEntries = useResourcePerformanceObserver('api.velocityweather.com/v1');
  useEffect(() => {
    if (resourcePerformanceEntries.length > 0) {
      for (const entry of resourcePerformanceEntries) {
        /*
        we assume that this is cached if the transferSize is 0 and the duration(fetch start- response end) is less than 5ms
        For non-CORS requests, the transferSize is 0 for cache hits. But this is a CORS fetch, thus lacks most of the properties of a normal fetch
        performance entry. This is why we need to check the duration and assume cache hit.
        */

        if (entry.transferSize === 0 && entry.duration <= 5) {
          // This is a cached entry, ignore it
          continue;
        }
        /*
        samples:
           1. https://api.velocityweather.com/v1/Rf55lhYIwpcS/meta/tiles/product-instances/C09-0x0374-0/Standard-Mercator.jsonp?ts=1745292110&sig=hLYvuSX8L8EPiDke2SC5jnT0_Ws=&page_size=1&callback=?
           2. https://api.velocityweather.com/v1/Rf55lhYIwpcS/tms/1.0.0/north-american-radar+Standard-Mercator+2025-04-22T04:06:40Z/6/7/46.png?ts=1745294959&sig=4NFmKzY4VKVfIdNIsdFtcgyiYio=&valid_time=2025-04-22T04:06:40Z
        */

        const UrlParts = entry.name
          .replace('http://', '')
          .replace('https://', '')
          .split('/');

        const callType = UrlParts[3];

        if (UrlParts.length < 5) {
          // Not a valid velocity weather API call
          continue;
        }

        if (callType == 'meta' && UrlParts.length > 6) {
          //meta query
          datadogLogs.logger.info('velocity-weather-api-call', {
            fullUrl: entry.name,
            callType,
            userId: isPublicUser ? 'public' : userData?.id,
            config: UrlParts[UrlParts.length - 2],
            projection: UrlParts[UrlParts.length - 1].split('.')[0],
            duration: entry.duration
          });
        } else if (callType == 'tms' && UrlParts.length > 8) {
          //tile fetching query
          const fullProductConfig = UrlParts[UrlParts.length - 4];
          const config = fullProductConfig.split('+')[0];
          const projection = fullProductConfig.split('+')[1];

          const zoom = UrlParts[UrlParts.length - 3];
          const gridX = UrlParts[UrlParts.length - 2];
          const gridY = UrlParts[UrlParts.length - 1].split('.')[0];

          datadogLogs.logger.info('velocity-weather-api-call', {
            /* For unique tile count */

            fullUrl: entry.name,
            callType,
            gridX,
            gridY,
            zoom,
            userId: isPublicUser ? 'public' : userData?.id,
            config: config,
            projection,
            duration: entry.duration
          });
        } else {
          // Other API calls
          datadogLogs.logger.info('velocity-weather-api-call', {
            fullUrl: entry.name,
            callType: 'unknown',
            userId: isPublicUser ? 'public' : userData?.id,
            duration: entry.duration
          });
        }
      }
    }
  }, [resourcePerformanceEntries]);
  return null;
};

export default VelocityWeatherLogger;
